import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Menu, MessageSquare } from 'lucide-react';

import AnalyticsApiService, {
  AppUserDashboardSortBy,
  AppUserDashboardSortDir,
  AppUserDashboardSummary,
  AppUserDashboardSummaryResponse,
  AppUserUpdate,
  PaginatedConversations,
} from '../services/analyticsApi';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

type SearchMode = 'conversations' | 'users';

export interface AppUsersSearchModeProps {
  onClose: () => void;
  onModeChange: (mode: SearchMode) => void;
  onOpenConversationSearch: (search: string) => void;
}

type Pagination = AppUserDashboardSummaryResponse['pagination'];

const EMPTY_FORM = {
  username: '',
  phone: '',
  fname: '',
  lname: '',
  location: '',
  onboarded: false,
};

export function AppUsersSearchMode({ onClose, onModeChange, onOpenConversationSearch }: AppUsersSearchModeProps) {
  const { user: currentUser } = useAuth();

  const canManageAppUsers = useMemo(() => {
    // Allow either permission key; hasPermission already treats is_admin and all_features as allow-all.
    return hasPermission(currentUser, 'manage_users') || hasPermission(currentUser, 'user_management');
  }, [currentUser]);

  const [users, setUsers] = useState<AppUserDashboardSummary[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editingUser, setEditingUser] = useState<AppUserDashboardSummary | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deletingUser, setDeletingUser] = useState<AppUserDashboardSummary | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [userChatsTarget, setUserChatsTarget] = useState<AppUserDashboardSummary | null>(null);
  const [userChatsLoading, setUserChatsLoading] = useState(false);
  const [userChatsError, setUserChatsError] = useState<string | null>(null);
  const [userChatsResponse, setUserChatsResponse] = useState<PaginatedConversations | null>(null);
  const [userChatsSearchTerm, setUserChatsSearchTerm] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy, setSortBy] = useState<AppUserDashboardSortBy>('joined_at');
  const [sortDir, setSortDir] = useState<AppUserDashboardSortDir>('desc');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [joinedDaysMin, setJoinedDaysMin] = useState('');
  const [joinedDaysMax, setJoinedDaysMax] = useState('');
  const [groupChatMin, setGroupChatMin] = useState('');
  const [groupChatMax, setGroupChatMax] = useState('');
  const [messagesSentMin, setMessagesSentMin] = useState('');
  const [messagesSentMax, setMessagesSentMax] = useState('');
  const [messagesExchangedMin, setMessagesExchangedMin] = useState('');
  const [messagesExchangedMax, setMessagesExchangedMax] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const resetPaging = useCallback(() => setOffset(0), []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const parseOptionalInt = useCallback((value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return undefined;
    return Math.max(0, Math.trunc(parsed));
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const age_min = parseOptionalInt(ageMin);
      const age_max = parseOptionalInt(ageMax);
      const joined_days_min = parseOptionalInt(joinedDaysMin);
      const joined_days_max = parseOptionalInt(joinedDaysMax);
      const group_chat_min = parseOptionalInt(groupChatMin);
      const group_chat_max = parseOptionalInt(groupChatMax);
      const messages_sent_min = parseOptionalInt(messagesSentMin);
      const messages_sent_max = parseOptionalInt(messagesSentMax);
      const messages_exchanged_min = parseOptionalInt(messagesExchangedMin);
      const messages_exchanged_max = parseOptionalInt(messagesExchangedMax);

      const data = await AnalyticsApiService.getAppUsersAnalytics({
        limit,
        offset,
        sort_by: sortBy,
        sort_dir: sortDir,
        q: debouncedQuery || undefined,
        include_deleted: includeDeleted,
        gender: genderFilter.trim() ? genderFilter.trim() : undefined,
        age_min,
        age_max,
        joined_days_min,
        joined_days_max,
        group_chat_min,
        group_chat_max,
        messages_sent_min,
        messages_sent_max,
        messages_exchanged_min,
        messages_exchanged_max,
      });

      setUsers(data.items);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [
    ageMax,
    ageMin,
    debouncedQuery,
    genderFilter,
    groupChatMax,
    groupChatMin,
    includeDeleted,
    joinedDaysMax,
    joinedDaysMin,
    limit,
    messagesExchangedMax,
    messagesExchangedMin,
    messagesSentMax,
    messagesSentMin,
    offset,
    parseOptionalInt,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleOpenEditor = useCallback((user: AppUserDashboardSummary) => {
    setEditingUser(user);
    setForm({
      username: user.username ?? '',
      phone: user.phone ?? '',
      fname: user.fname ?? '',
      lname: user.lname ?? '',
      location: user.location ?? '',
      onboarded: Boolean(user.onboarded),
    });
  }, []);

  const handleCloseEditor = useCallback(() => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
  }, []);

  const handleSaveUser = useCallback(async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      const payload: AppUserUpdate = {
        username: form.username,
        phone: form.phone,
        fname: form.fname,
        lname: form.lname,
        location: form.location || null,
        onboarded: form.onboarded,
      };
      await AnalyticsApiService.updateAppUser(editingUser.id, payload);
      await loadUsers();
      handleCloseEditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user.');
    } finally {
      setSaving(false);
    }
  }, [editingUser, form, handleCloseEditor, loadUsers]);

  const handleConfirmDelete = useCallback((user: AppUserDashboardSummary) => {
    setDeletingUser(user);
  }, []);

  const handleDeleteUser = useCallback(async () => {
    if (!deletingUser) return;
    setDeleteLoading(true);
    try {
      await AnalyticsApiService.deleteAppUser(deletingUser.id);
      await loadUsers();
      setDeletingUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deletingUser, loadUsers]);

  const handleOpenUserChats = useCallback(async (user: AppUserDashboardSummary) => {
    setUserChatsTarget(user);
    setUserChatsLoading(true);
    setUserChatsError(null);
    setUserChatsResponse(null);
    setUserChatsSearchTerm(null);
    try {
      const candidates = buildConversationSearchCandidates(user);
      let last: PaginatedConversations | null = null;

      for (const search of candidates) {
        // Uses the same endpoint as the /conversations page (Agent Service search proxy)
        const resp = await AnalyticsApiService.getConversations({ page: 1, limit: 25, search });
        last = resp;
        if ((resp.items || []).length > 0) {
          setUserChatsResponse(resp);
          setUserChatsSearchTerm(search);
          return;
        }
      }

      // Nothing matched - keep the last response for consistency (usually empty)
      setUserChatsResponse(last);
      setUserChatsSearchTerm(candidates[0] ?? null);
    } catch (err) {
      setUserChatsError(err instanceof Error ? err.message : 'Failed to load chats for user.');
    } finally {
      setUserChatsLoading(false);
    }
  }, []);

  const handleCloseUserChats = useCallback(() => {
    setUserChatsTarget(null);
    setUserChatsError(null);
    setUserChatsResponse(null);
    setUserChatsLoading(false);
    setUserChatsSearchTerm(null);
  }, []);

  const handleResetFilters = useCallback(() => {
    setQuery('');
    setSortBy('joined_at');
    setSortDir('desc');
    setGenderFilter('');
    setAgeMin('');
    setAgeMax('');
    setJoinedDaysMin('');
    setJoinedDaysMax('');
    setGroupChatMin('');
    setGroupChatMax('');
    setMessagesSentMin('');
    setMessagesSentMax('');
    setMessagesExchangedMin('');
    setMessagesExchangedMax('');
    setIncludeDeleted(false);
    setLimit(50);
    setOffset(0);
  }, []);

  const totalRecords = pagination?.total ?? users.length;

  const handleModeSwitch = useCallback(
    (mode: SearchMode) => {
      onModeChange(mode);
    },
    [onModeChange],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-8 pt-6 pb-5 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            aria-label="Back to conversations"
            className="p-2 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-md hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-['SF_Pro_Display',_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xl">
                  Users
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Search and filter Series app users (dashboard analytics view).
                </p>
              </div>

              <div className="shrink-0 flex items-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/60 backdrop-blur-md p-1">
                <button
                  type="button"
                  onClick={() => handleModeSwitch('conversations')}
                  aria-label="Switch to conversation search"
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
                  )}
                >
                  Conversations
                </button>
                <button
                  type="button"
                  onClick={() => handleModeSwitch('users')}
                  aria-label="Switch to user search"
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                    'bg-gray-900 text-white dark:bg-white dark:text-gray-900',
                  )}
                >
                  Users
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
          {/* Filters */}
          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-900/50 backdrop-blur-xl shadow-sm p-6 space-y-4">
            <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)_minmax(140px,1fr)_auto] md:items-end">
              <Field label="Search">
                <input
                  type="text"
                  value={query}
                  placeholder="Phone, username, name, id..."
                  onChange={(e) => {
                    setQuery(e.target.value);
                    resetPaging();
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Sort by">
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value as AppUserDashboardSortBy);
                    setOffset(0);
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="joined_at">Joined at</option>
                  <option value="name">Name</option>
                  <option value="age">Age</option>
                  <option value="group_chats">Group chats</option>
                  <option value="messages_sent">Messages sent</option>
                  <option value="messages_exchanged">Messages exchanged</option>
                </select>
              </Field>
              <Field label="Direction">
                <select
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value as AppUserDashboardSortDir);
                    setOffset(0);
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="desc">Desc</option>
                  <option value="asc">Asc</option>
                </select>
              </Field>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowFilters((v) => !v)}
                  aria-expanded={showFilters}
                  className="h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {showFilters ? 'Hide filters' : 'More filters'}
                </button>
              </div>
            </div>

            {showFilters && (
              <div className="grid gap-4 md:grid-cols-3">
                <Field label="Gender">
                  <input
                    type="text"
                    value={genderFilter}
                    placeholder="Any"
                    onChange={(e) => {
                      setGenderFilter(e.target.value);
                      resetPaging();
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </Field>

                <Field label="Age range">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={ageMin}
                      placeholder="Min"
                      onChange={(e) => {
                        setAgeMin(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={ageMax}
                      placeholder="Max"
                      onChange={(e) => {
                        setAgeMax(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </Field>

                <Field label="Days since join">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={joinedDaysMin}
                      placeholder="Min"
                      onChange={(e) => {
                        setJoinedDaysMin(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={joinedDaysMax}
                      placeholder="Max"
                      onChange={(e) => {
                        setJoinedDaysMax(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </Field>

                <Field label="Group chats">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={groupChatMin}
                      placeholder="Min"
                      onChange={(e) => {
                        setGroupChatMin(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={groupChatMax}
                      placeholder="Max"
                      onChange={(e) => {
                        setGroupChatMax(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </Field>

                <Field label="Messages sent">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={messagesSentMin}
                      placeholder="Min"
                      onChange={(e) => {
                        setMessagesSentMin(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={messagesSentMax}
                      placeholder="Max"
                      onChange={(e) => {
                        setMessagesSentMax(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </Field>

                <Field label="Messages exchanged">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      inputMode="numeric"
                      value={messagesExchangedMin}
                      placeholder="Min"
                      onChange={(e) => {
                        setMessagesExchangedMin(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      inputMode="numeric"
                      value={messagesExchangedMax}
                      placeholder="Max"
                      onChange={(e) => {
                        setMessagesExchangedMax(e.target.value);
                        resetPaging();
                      }}
                      className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </Field>

                <Field label="Include deleted">
                  <button
                    type="button"
                    onClick={() => {
                      setIncludeDeleted((value) => !value);
                      resetPaging();
                    }}
                    className={clsx(
                      'h-10 rounded-lg border px-3 text-left text-sm transition-colors',
                      includeDeleted
                        ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-400/60 dark:bg-emerald-500/10 dark:text-emerald-100'
                        : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200',
                    )}
                  >
                    {includeDeleted ? 'Yes' : 'No'}
                  </button>
                </Field>

                <Field label="Page size">
                  <select
                    value={String(limit)}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setOffset(0);
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </Field>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium">{totalRecords.toLocaleString()} results</span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Reset filters
                </button>
                <button
                  type="button"
                  onClick={loadUsers}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Results table */}
          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-700/50 bg-white/70 dark:bg-gray-900/50 backdrop-blur-xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-200/60 dark:border-gray-700/50 px-5 py-4">
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Total users</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">{totalRecords.toLocaleString()} records</p>
              </div>
              <button
                type="button"
                onClick={loadUsers}
                className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-900/30 dark:text-gray-400">
                  <tr>
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3">Username</th>
                    <th className="px-5 py-3">Phone</th>
                    <th className="px-5 py-3">Gender</th>
                    <th className="px-5 py-3">Age</th>
                    <th className="px-5 py-3">Joined</th>
                    <th className="px-5 py-3 text-right">Group chats</th>
                    <th className="px-5 py-3 text-right">Msgs sent</th>
                    <th className="px-5 py-3 text-right">Msgs exchanged</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/50">
                  {loading && (
                    <tr>
                      <td colSpan={11} className="px-5 py-8 text-center text-gray-600 dark:text-gray-400">
                        Loading users...
                      </td>
                    </tr>
                  )}
                  {!loading && users.length === 0 && (
                    <tr>
                      <td colSpan={11} className="px-5 py-8 text-center text-gray-600 dark:text-gray-400">
                        No users found.
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{formatName(user)}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{user.id}</div>
                        </td>
                        <td className="px-5 py-3 text-gray-900 dark:text-white">{user.username || '—'}</td>
                        <td className="px-5 py-3 text-gray-900 dark:text-white">{user.phone || '—'}</td>
                        <td className="px-5 py-3 text-gray-900 dark:text-white">{user.gender || '—'}</td>
                        <td className="px-5 py-3 text-gray-900 dark:text-white">{user.age ?? '—'}</td>
                        <td className="px-5 py-3">
                          <div className="text-gray-900 dark:text-white">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{formatTimeSince(user.created_at)}</div>
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {user.group_chat_count.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {user.total_messages_sent.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {user.total_messages_exchanged.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 space-x-2">
                          <span
                            className={clsx(
                              'inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold',
                              user.onboarded
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-200',
                            )}
                          >
                            {user.onboarded ? 'Onboarded' : 'Invited'}
                          </span>
                          {user.is_deleted && (
                            <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-100">
                              Deleted
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label={`Actions for ${formatName(user)}`}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800 transition-colors"
                              >
                                <Menu className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => handleOpenUserChats(user)}>
                                <MessageSquare className="h-4 w-4" />
                                View chats
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => handleOpenEditor(user)}>Edit</DropdownMenuItem>
                              {canManageAppUsers && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem variant="destructive" onSelect={() => handleConfirmDelete(user)}>
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200/60 bg-white/70 px-4 py-3 text-xs text-gray-600 dark:border-gray-700/50 dark:bg-gray-900/50 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={loading || offset === 0}
                  onClick={() => setOffset((value) => Math.max(0, value - pagination.limit))}
                  className="h-8 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={loading || pagination.offset + pagination.returned >= pagination.total}
                  onClick={() => setOffset((value) => value + pagination.limit)}
                  className="h-8 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
              <div className="font-medium">
                {pagination.total === 0
                  ? '0 results'
                  : `Showing ${pagination.offset + 1}-${pagination.offset + pagination.returned} of ${pagination.total}`}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit user</h2>
              <button
                type="button"
                onClick={handleCloseEditor}
                className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="First name">
                <input
                  value={form.fname}
                  onChange={(e) => setForm((prev) => ({ ...prev, fname: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Last name">
                <input
                  value={form.lname}
                  onChange={(e) => setForm((prev) => ({ ...prev, lname: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Username">
                <input
                  value={form.username}
                  onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Phone">
                <input
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Location">
                <input
                  value={form.location}
                  onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>
              <Field label="Onboarded">
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, onboarded: !prev.onboarded }))}
                  className={clsx(
                    'h-10 rounded-lg border px-3 text-left text-sm transition-colors',
                    form.onboarded
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-100'
                      : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200',
                  )}
                >
                  {form.onboarded ? 'Yes' : 'No'}
                </button>
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCloseEditor}
                className="h-10 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveUser}
                className="h-10 px-4 rounded-lg bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-300">Delete user</h2>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete {formatName(deletingUser)} ({deletingUser.id})? This action can be reversed by reactivating the
              user later.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeletingUser(null)}
                className="h-10 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteLoading}
                onClick={handleDeleteUser}
                className="h-10 px-4 rounded-lg bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User chats modal */}
      {userChatsTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Chats for {formatName(userChatsTarget)}</h2>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  User ID: <span className="font-mono">{userChatsTarget.id}</span>
                </p>
                {userChatsSearchTerm && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    Search: <span className="font-mono">{userChatsSearchTerm}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const term = userChatsSearchTerm || buildConversationSearchCandidates(userChatsTarget)[0] || '';
                    onOpenConversationSearch(term);
                    handleCloseUserChats();
                  }}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Go to conversation search
                </button>
                <button
                  type="button"
                  onClick={handleCloseUserChats}
                  className="h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="mt-4">
              {userChatsLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading chats...</div>}
              {userChatsError && <div className="text-sm text-red-700 dark:text-red-200">{userChatsError}</div>}
              {!userChatsLoading && !userChatsError && (
                <div className="space-y-2">
                  {(userChatsResponse?.items || []).length === 0 ? (
                    <div className="text-sm text-gray-600 dark:text-gray-400">No conversations found for this user.</div>
                  ) : (
                    (userChatsResponse?.items || []).map((c: any) => (
                      <div
                        key={c.id}
                        className="rounded-xl border border-gray-200/60 dark:border-gray-700/50 bg-gray-50/40 dark:bg-gray-950/20 p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              Conversation {c.id} {c.is_group ? '(Group)' : ''}
                            </div>
                            <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                              {(c.participants || [])
                                .map((p: any) => p?.name || p?.phone || p?.id)
                                .filter(Boolean)
                                .join(', ') || '—'}
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {typeof c.message_count === 'number' ? `${c.message_count} msgs` : ''}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</div>
      {children}
    </div>
  );
};

const formatName = (user: Pick<AppUserDashboardSummary, 'fname' | 'lname' | 'username' | 'phone' | 'id'>): string => {
  const parts = [user.fname, user.lname].filter((p) => Boolean(p && p.trim().length > 0)) as string[];
  const full = parts.join(' ');
  if (full) return full;
  if (user.username) return user.username;
  if (user.phone) return user.phone;
  return user.id;
};

const formatTimeSince = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '—';
  const diffMs = Date.now() - ts;
  if (!Number.isFinite(diffMs) || diffMs < 0) return '—';
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const buildConversationSearchCandidates = (user: Pick<AppUserDashboardSummary, 'id' | 'phone' | 'fname' | 'lname' | 'username'>): string[] => {
  const candidates: string[] = [];

  const addCandidate = (value?: string | null) => {
    if (!value) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (candidates.includes(trimmed)) return;
    candidates.push(trimmed);
  };

  const fullName = [user.fname, user.lname]
    .map((v) => (v || '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();

  // Prefer "human searchable" identifiers first (these match Agent Service search best).
  addCandidate(user.phone ?? undefined);
  if (user.phone) {
    const digits = user.phone.replace(/\D/g, '');
    addCandidate(digits);
    if (digits.length === 10) addCandidate(`+1${digits}`);
    if (digits.length === 11 && digits.startsWith('1')) addCandidate(`+${digits}`);
  }
  addCandidate(fullName || undefined);
  addCandidate(user.username ?? undefined);

  // Fallback: UUID (rarely matches conversation search, but keep as last resort)
  addCandidate(user.id);

  return candidates;
};


